import { Select, Statistic, Table } from 'antd';
import commaNumber from 'comma-number';
import React, { memo, useMemo } from 'react';
import CountUp from 'react-countup';
import styled from 'styled-components';

import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import {
  type CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
} from '@/entities/chart/model/constants';
import { getChartMetricLabel } from '@/features/(charts)/chart-metric-policy';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import { customScrollbar } from '@/styles/style';

import ChartTableConfigProvider from '../chart-table-config-provider';
import useChartTotalTableData from './useChartTotalTableData';

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  onHistoricalChange: (value: boolean) => void;
  height?: number;
  dashboardCardInfo?: DashboardCardInfoT;
  yAxisTitle?: string;
};

const TotalTable = (props: Props) => {
  const { columns, dataSource } = useChartTotalTableData(props);

  const tableData = props.tableData as ChartSingleTableRes;

  const total = useMemo(() => {
    if (
      props.chart.series[0]?.options?.view_type ===
      CHART_METRIC_VIEW_TYPE.PERCENTAGE
    )
      return 100;
    return tableData.tables[0]?.reduce((acc, cur) => {
      acc += cur.value;
      return acc;
    }, 0);
  }, [props.chart.series, tableData.tables]);

  const countUpDisplayState = useMemo(() => {
    return tableData.tables[0]?.length === 1 && !tableData.compare_tables;
  }, [tableData.compare_tables, tableData.tables]);

  return (
    <Style style={{ height: props.height || 360 }}>
      <Flex j="flex-end">
        <Select
          style={{ width: 120 }}
          value={props.chart.historical.value}
          options={[
            {
              label: 'Total',
              value: false,
            },
            {
              label: 'Historical',
              value: true,
            },
          ]}
          onChange={props.onHistoricalChange}
        />
      </Flex>
      <Space h={16} />
      {countUpDisplayState ? (
        <CountUpDisplay
          metric={props.chart.series[0]?.measure.metric}
          total={total}
          viewType={
            props.chart.series[0]?.options?.view_type ||
            CHART_METRIC_VIEW_TYPE.COUNT
          }
        />
      ) : (
        <ChartTableConfigProvider>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            bordered
            scroll={{
              x: 'max-content',
            }}
          />
        </ChartTableConfigProvider>
      )}
    </Style>
  );
};

const Style = styled(VFlex)`
  position: relative;
  width: 100%;
  flex: 1;
  overflow: auto;
  ${customScrollbar};

  .ant-table-thead th {
    background-color: ${({ theme }) => theme.colors.gray200} !important;
    padding: 10px 16px !important;
  }

  .ant-table-row td {
    background-color: ${({ theme }) => theme.colors.gray100};
    padding: 12px 16px !important;
  }

  .ellipsis {
    font-size: 12px;
  }
`;

type CountUpDisplayProps = {
  metric: CHART_METRIC;
  total: number;
  viewType: CHART_METRIC_VIEW_TYPE;
};

const CountUpDisplay = memo((props: CountUpDisplayProps) => {
  const countUpFormatter = useMemo(() => {
    return (value: number | string) => (
      <CountUp
        end={Number(value)}
        duration={0.5}
        formattingFn={(value) => {
          if (props.viewType === CHART_METRIC_VIEW_TYPE.PERCENTAGE)
            return `${commaNumber(value.toFixed(1))}%`;
          return commaNumber(value);
        }}
      />
    );
  }, [props.viewType]);

  return (
    <React.Fragment>
      <CenterFlex f={1}>
        <Flex width="250px" j="center">
          <Statistic
            title={getChartMetricLabel(props.metric)}
            value={props.total}
            formatter={countUpFormatter}
            valueStyle={{
              fontSize: 42,
            }}
          />
        </Flex>
      </CenterFlex>
    </React.Fragment>
  );
});

export default memo(TotalTable);
